import { flatten, uniqBy } from 'lodash';
import { useIntl } from 'react-intl';
import { AdvanceFilters } from 'sonata-react-components';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { FiltersSelectors } from './FiltersSelectors/FiltersSelectors';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';

type Props = {
  dealPublishers?: Record<string, any>[];
  deals: DealInterface[];
  isLoading: boolean;
  onChange: (filters: { advanceFilters: { id: string; value: any }[]; text?: string }) => void;
  onInputFilterChange: (text: string) => void;
  sites: Record<string, any>;
};

export function DealsFilters({ dealPublishers, deals, isLoading, onChange, onInputFilterChange, sites }: Props) {
  const { formatMessage } = useIntl();

  const countryOptions = uniqBy(
    deals?.map(deal => {
      if (!deal.country) {
        return {
          id: 'N/A',
          label: 'N/A'
        };
      }
      const country = worldCountries.find(c => c.cca2 === deal.country.toUpperCase());
      return {
        id: deal.country,
        label: country?.name.common
      };
    }),
    'id'
  );

  const creativeSizeOptions = uniqBy(
    flatten(
      deals?.map(deal => {
        if (!deal.creatives || deal.creatives.length === 0) {
          return {
            id: 'N/A',
            label: 'N/A'
          };
        }
        return deal.creatives.map(creat => ({
          id: creat,
          label: formatMessage({
            defaultMessage: creat,
            id: `appAndSiteMarketplace.dealSelection.list.creativesSize.${creat}`
          })
        }));
      })
    ),
    'id'
  );

  const appAndSitesOptions = uniqBy(
    flatten(
      deals?.map(deal => {
        if (!deal.placements || deal.placements.length === 0) {
          return {
            id: 'N/A',
            label: 'N/A'
          };
        }
        return deal.placements.map(placement => {
          const site = sites?.items?.find((s: Record<string, any>) => s._id === placement.site);
          if (site) {
            return {
              id: site.name,
              label: `${site.name}-${site.platform === 'web' ? 'site' : 'app'}`
            };
          }
          return {
            id: 'N/A',
            label: 'N/A'
          };
        });
      })
    ),
    'id'
  );

  const supplySourceOptions = uniqBy(
    deals?.map(deal => ({
      id: deal.origin.name,
      label: deal.origin.name.charAt(0).toUpperCase() + deal.origin.name.slice(1)
    })),
    'id'
  );

  const sourceTypeOptions = uniqBy(
    deals?.map(deal => ({
      id: deal.sourceType,
      label: deal.sourceType.toString().charAt(0).toUpperCase() + deal.sourceType.toString().slice(1)
    })),
    'id'
  );

  const publisherOptions = uniqBy(
    deals?.map(deal => {
      const publisherInfo = dealPublishers?.find(publisher => publisher.id === deal._id);
      return {
        id: publisherInfo?.publisher,
        label: publisherInfo?.publisher
      };
    }),
    'id'
  );

  return (
    <AdvanceFilters
      className="dealSelection__filters"
      disabled={isLoading}
      expanded
      onChange={onChange}
      onInputFilter={onInputFilterChange}
      placeholder={formatMessage({
        defaultMessage: 'Search by id or name...',
        id: 'appAndSiteMarketplace.dealSelection.filters.search'
      })}
    >
      <FiltersSelectors
        appAndSitesOptions={appAndSitesOptions as SelectorOptionPropsInterface[]}
        countryOptions={countryOptions}
        creativeSizeOptions={creativeSizeOptions}
        isLoading={isLoading}
        publisherOptions={publisherOptions}
        sourceTypeOptions={sourceTypeOptions}
        supplySourceOptions={supplySourceOptions}
      />
    </AdvanceFilters>
  );
}
